import React from "react";
import { Box, Chip, Divider, IconButton, TablePagination, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Link } from "react-router-dom";

import RecordCard from "../../ShipmentRequest/ShipmentComponents/RecordCard";
import { useAuth, useTablePagination } from "hooks";
import { useDeliveryDetailContext } from "contexts/DeliveryDetailContext";
import { useLazyListShipmentsByDeliveryIdQuery } from "store/services/deliveryApi";

const FieldRow = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 0.25 }}>
    <Box sx={{ width: "40%", color: "text.primary", fontSize: "0.7rem", fontWeight: 600 }}>
      {label}
    </Box>
    <Box sx={{ width: "60%", fontSize: "0.74rem", fontWeight: 500 }}>
      {value ?? "N/A"}
    </Box>
  </Box>
);

const statusColors = {
  Pending: { backgroundColor: "#F8C471", textColor: "#D35400" },
  Collected: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
  Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
  Delivered: { backgroundColor: "#DFF7DF", textColor: "green" },
  default: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
};

const DeliveryShipmentsCompact = () => {
  const { deliveryDetail } = useDeliveryDetailContext();
  const { isVendor, isCustomer, isDriver } = useAuth();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [fetchShipments, { data: shipmentRes, isLoading, isFetching }] =
    useLazyListShipmentsByDeliveryIdQuery();

  React.useEffect(() => {
    if (!deliveryDetail?._id) return;
    fetchShipments({
      deliveryId: deliveryDetail?._id,
      page: currentPage,
      limit: perPage,
    });
  }, [currentPage, fetchShipments, perPage, deliveryDetail?._id]);

  const basePath = isVendor
    ? "/app/vendor/shipment"
    : isCustomer
      ? "/app/customer/shipment"
      : isDriver
        ? "/app/driver/shipment"
        : "/app/crm/shipment";

  const shipments = shipmentRes?.data || [];

  if (!shipmentRes) {
    return (
      <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", textAlign: "center", py: 2 }}>
        Loading shipments...
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {isLoading || isFetching ? (
        <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", textAlign: "center", py: 2 }}>
          Loading shipments...
        </Typography>
      ) : shipments.length === 0 ? (
        <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", textAlign: "center", py: 2 }}>
          No shipments found.
        </Typography>
      ) : (
        shipments.map((row) => {
          const style = statusColors[row?.status] || statusColors.default;
          return (
            <RecordCard key={row?._id}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Link
                  to={`${basePath}/${row?._id}/detail`}
                  className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                  title={row?.shipmentId}
                >
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                    {row?.shipmentId?.replace(/(.{4})/g, "$1-").slice(0, -1) || "N/A"}
                  </Typography>
                </Link>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Chip
                    label={row?.status || "N/A"}
                    size="small"
                    sx={{
                      backgroundColor: style.backgroundColor,
                      color: style.textColor,
                      height: 18, fontSize: "0.6rem", fontWeight: "bold",
                    }}
                  />
                  <Link to={`${basePath}/${row?._id}/detail`}>
                    <IconButton size="small" aria-label="View Shipment" color="primary">
                      <InfoOutlined sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Link>
                </Box>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              {!isVendor && (
                <FieldRow label="Sender" value={row?.vendor?.shopName || row?.customer?.profileName} />
              )}
              <FieldRow
                label="Receiver"
                value={[row?.receiver?.customerUser?.name, row?.receiver?.phone].filter(Boolean).join(" • ")}
              />
              <FieldRow
                label="Route"
                value={`${row?.sourceBranch?.name || "N/A"} → ${row?.destinationBranch?.name || "N/A"}`}
              />
              <FieldRow label="Amount" value={`Rs. ${row?.shipmentAmount || "0.00"}`} />
            </RecordCard>
          );
        })
      )}
      <TablePagination
        component="div"
        count={shipmentRes?.totalItems || 0}
        page={(currentPage || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
        rowsPerPage={perPage}
        onRowsPerPageChange={(e) => handlePerRowsChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{ "& .MuiTablePagination-toolbar": { minHeight: 40 }, "& .MuiTablePagination-spacer": { flex: 0 } }}
      />
    </Box>
  );
};

export default DeliveryShipmentsCompact;
